import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import localeFr from '@angular/common/locales/fr';
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import {
  BrowserModule,
  Meta,
  provideClientHydration,
  withEventReplay,
  withIncrementalHydration,
} from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withComponentInputBinding, withInMemoryScrolling } from '@angular/router';
import { CacheInterceptor, MockInterceptor, ResponseInterceptor } from '@api/interceptors';
import { environment } from '@environments/environment';
import { AuthenticationService } from '@services/authentication';
import { CommonService } from '@services/common';
import { Common } from '@services/common/model';
import { ContextService } from '@services/context';
import { IconsService } from '@services/icons';
import { GlobalErrorHandler, LogsService } from '@services/logs';
import { UrlService } from '@services/url';
import { RoutesPath } from '@shared/constants';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { APP_ROUTES } from './app.routes';

registerLocaleData(localeFr);

const ROBOTS_INDEX = 'index, follow';
const ROBOTS_NOINDEX = 'noindex, follow, nosnippet, noimageindex, notranslate';

function updateRobotsMeta(meta: Meta, content: string): void {
  meta.removeTag("name='robots'");
  meta.addTag({ name: 'robots', content });
}

function initializeMetaRobots(): void {
  const meta = inject(Meta);
  const robotsContent = environment.allowIndexing ? ROBOTS_INDEX : ROBOTS_NOINDEX;
  updateRobotsMeta(meta, robotsContent);
}

// An initializer, not a route: the menu, footer and scripts of app.component.html render before any route is evaluated.
function handleAuthCallback(): Promise<never> | void {
  if (inject(ContextService).isBrowser() && location.pathname === `/${RoutesPath.AUTH_CALLBACK}`) {
    return inject(AuthenticationService).handleRedirectCallback();
  }
}

function initializeApp(): () => Observable<Common> {
  const commonService = inject(CommonService);
  inject(UrlService).monitoring();
  inject(IconsService).loadOnCache();
  inject(LogsService).updateLogLevelFromSession();
  return () => {
    const isPrefetchCmsEnabled = environment.isPrefetchCmsEnabled;
    if (isPrefetchCmsEnabled) {
      return commonService.readPrefetch<Common>('common', { cacheExpirationTime: 600 }).pipe(
        tap((common) => {
          commonService.setCommon(common);
        })
      );
    } else {
      return commonService.getRoot({ cacheExpirationTime: 600 }).pipe(
        tap((common) => {
          commonService.setCommon(common);
        })
      );
    }
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    importProvidersFrom(BrowserModule),
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    provideClientHydration(withIncrementalHydration(), withEventReplay()),
    provideRouter(
      APP_ROUTES,
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'disabled',
      }),
      withComponentInputBinding()
    ),
    { provide: LOCALE_ID, useValue: 'fr-FR' },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    // Zoneless: without these listeners, window 'error' and 'unhandledrejection' events never reach the ErrorHandler
    provideBrowserGlobalErrorListeners(),
    provideAppInitializer(handleAuthCallback),
    provideAppInitializer(initializeMetaRobots),
    provideAppInitializer(() => initializeApp()()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MockInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CacheInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ResponseInterceptor,
      multi: true,
    },
  ],
};
